import { useCallback, useMemo } from "react";

import { useTranslation } from "react-i18next";

import { AgeGroupEnum, GenderValEnum } from "shared/types";
import { TableColumnFields } from "shared/types/BulkTeamTable";
import type { CreateClubTeamModel } from "shared/types/TeamModel";
import { validateEmail, validateLabel } from "utils/serviceUtils/validators";

import { BulkTableRowStatuses } from "../Components/BulkTable";
import type { TableTeamData } from "../Components/BulkTeamTable";

// Precompile regex patterns for better performance
const AGE_REGEX = /^(?:[UuOo](?:[1-9]|[1-9][0-9]|100)|Open|open)$/;
const VALID_GENDER_VALUES = new Set(["male", "female", "boys", "girls", "m", "b", "g", "f"]);
const MALE_GENDER_VALUES = new Set(["boys", "male", "m", "b"]);
const FEMALE_GENDER_VALUES = new Set(["girls", "female", "f", "g"]);

const useBulkTeamTableValidation = ({ levelOptions }: { levelOptions?: { label: string; value: string }[] }) => {
	const { t } = useTranslation();

	// Memoize required fields configuration
	const requiredFields = useMemo(
		(): {
			[field in TableColumnFields]: boolean;
		} => ({
			[TableColumnFields.INDEX]: false,
			[TableColumnFields.NAME]: true,
			[TableColumnFields.AGE]: true,
			[TableColumnFields.GENDER]: true,
			[TableColumnFields.LEVEL]: true,
			[TableColumnFields.PRIMARY_COACH_NAME]: true,
			[TableColumnFields.PRIMARY_COACH_GENDER]: false,
			[TableColumnFields.PRIMARY_COACH_EMAIL]: true,
			[TableColumnFields.SECONDARY_COACH_NAME]: false,
			[TableColumnFields.SECONDARY_COACH_GENDER]: false,
			[TableColumnFields.SECONDARY_COACH_EMAIL]: false,
			[TableColumnFields.MANAGER_NAME]: false,
			[TableColumnFields.MANAGER_EMAIL]: false
		}),
		[]
	);

	// Create a memoized object with fast validation methods
	const methods = useMemo(() => {
		// Helper functions for better code organization and reusability
		const validateRequired = (val: string, fieldName: string, showErr: boolean) => {
			if (!val) {
				return showErr ? t("error:is_required", { field: t(fieldName) }) : false;
			}
			return null;
		};

		const validateNameField = (val: string, fieldKey: TableColumnFields, showErr: boolean, label = "name") => {
			if (!val && requiredFields[fieldKey]) {
				return showErr ? t("error:is_required", { field: t(label) }) : false;
			}

			// Length check
			if (val?.length < 2) {
				return showErr ? t("error:min_n_required", { count: 2 }) : false;
			}

			// Format validation
			if (val?.length) {
				const isValid = validateLabel(val?.trim());
				return isValid ? (showErr ? "" : true) : showErr ? t("error:invalid", { field: t(label) }) : false;
			}

			return showErr ? "" : true;
		};

		const validateGenderField = (val: string, fieldKey: TableColumnFields, showErr: boolean) => {
			if (!val && requiredFields[fieldKey]) {
				return showErr ? t("error:is_required", { field: t("gender") }) : false;
			}

			// Fast lookup using Set instead of array includes
			if (!val || !VALID_GENDER_VALUES.has(val.toLowerCase())) {
				return showErr ? t("error:invalid", { field: t("gender") }) : false;
			}

			return showErr ? "" : true;
		};

		const validateEmailField = (val: string, fieldKey: TableColumnFields, showErr: boolean) => {
			// Check if required
			if (!val && requiredFields[fieldKey]) {
				return showErr ? t("error:is_required", { field: t("email") }) : false;
			}

			// Validate email format if value exists
			if (val?.length) {
				const isValid = validateEmail(val?.trim());
				return isValid ? (showErr ? "" : true) : showErr ? t("error:invalid", { field: t("email") }) : false;
			}

			return showErr ? "" : true;
		};

		return {
			validateName: (val: string, showErr = false) => validateNameField(val, TableColumnFields.NAME, showErr),

			validateGender: (val: string, showErr = false) => validateGenderField(val, TableColumnFields.GENDER, showErr),

			validateAge: (val: string, showErr = false) => {
				// Fast path for empty required check
				const requiredCheck = validateRequired(val, "age", showErr);
				if (requiredCheck !== null) return requiredCheck;

				// Format validation
				if (val?.length) {
					const isValid = AGE_REGEX.test(val);
					return isValid ? (showErr ? "" : true) : showErr ? t("error:invalid", { field: t("age") }) : false;
				}

				return showErr ? "" : true;
			},

			validateLevel: (val: string, showErr = false) => {
				if (levelOptions) {
					if (!val && requiredFields[TableColumnFields.LEVEL]) {
						return showErr ? t("error:is_required", { field: t("level") }) : false;
					}

					if (!levelOptions.some(x => x.label === val || x.value === val)) {
						return showErr ? t("error:invalid", { field: t("level") }) : false;
					}

					return showErr ? "" : true;
				}

				return validateNameField(val, TableColumnFields.LEVEL, showErr, "level");
			},

			validatePrimaryCoachName: (val: string, showErr = false) =>
				validateNameField(val, TableColumnFields.PRIMARY_COACH_NAME, showErr),

			validatePrimaryCoachGender: (val: string, showErr = false) =>
				validateGenderField(val, TableColumnFields.PRIMARY_COACH_GENDER, showErr),

			validatePrimaryCoachEmail: (val: string, showErr = false) =>
				validateEmailField(val, TableColumnFields.PRIMARY_COACH_EMAIL, showErr),

			validateSecondaryCoachName: (val: string, showErr = false) =>
				validateNameField(val, TableColumnFields.SECONDARY_COACH_NAME, showErr),

			validateSecondaryCoachGender: (val: string, showErr = false) =>
				validateGenderField(val, TableColumnFields.SECONDARY_COACH_GENDER, showErr),

			validateSecondaryCoachEmail: (val: string, showErr = false) =>
				validateEmailField(val, TableColumnFields.SECONDARY_COACH_EMAIL, showErr),

			validateManagerName: (val: string, showErr = false) =>
				validateNameField(val, TableColumnFields.MANAGER_NAME, showErr),

			validateManagerEmail: (val: string, showErr = false) =>
				validateEmailField(val, TableColumnFields.MANAGER_EMAIL, showErr)
		};
	}, [levelOptions, requiredFields, t]);

	// Optimize row status checking for better performance
	const checkRowStatus = useCallback(
		(rowData: TableTeamData): BulkTableRowStatuses => {
			// Quick check for empty row
			if (!rowData || Object.keys(rowData).length === 0) {
				return BulkTableRowStatuses.DEFAULT;
			}

			const isEmptyRow = !Object.values(rowData).some(x => x !== undefined);
			if (isEmptyRow) {
				return BulkTableRowStatuses.DEFAULT;
			}

			// Fast path: check required fields first
			const requiredFieldKeys = Object.keys(requiredFields).filter(field => requiredFields[field as TableColumnFields]);

			for (const field of requiredFieldKeys) {
				const val = rowData[field]?.trim();
				if (!val) {
					return BulkTableRowStatuses.ERROR;
				}
			}

			// Check validation for fields that have values
			let hasWarning = false;

			// Only process fields that exist in the data
			const fieldsThatExist = Object.keys(rowData).filter(
				field => rowData[field] !== undefined && field in requiredFields
			);

			for (const field of fieldsThatExist) {
				const val = rowData[field]?.trim();
				if (val === undefined) continue;

				let isValid = true;

				switch (field) {
					case TableColumnFields.NAME:
						isValid = methods.validateName(val) as boolean;
						break;
					case TableColumnFields.AGE:
						isValid = methods.validateAge(val) as boolean;
						break;
					case TableColumnFields.GENDER:
						isValid = methods.validateGender(val) as boolean;
						break;
					case TableColumnFields.LEVEL:
						isValid = methods.validateLevel(val) as boolean;
						break;
					case TableColumnFields.PRIMARY_COACH_NAME:
						isValid = methods.validatePrimaryCoachName(val) as boolean;
						break;
					case TableColumnFields.PRIMARY_COACH_GENDER:
						isValid = methods.validatePrimaryCoachGender(val) as boolean;
						break;
					case TableColumnFields.PRIMARY_COACH_EMAIL:
						isValid = methods.validatePrimaryCoachEmail(val) as boolean;
						break;
					case TableColumnFields.SECONDARY_COACH_NAME:
						isValid = methods.validateSecondaryCoachName(val) as boolean;
						break;
					case TableColumnFields.SECONDARY_COACH_GENDER:
						isValid = methods.validateSecondaryCoachGender(val) as boolean;
						break;
					case TableColumnFields.SECONDARY_COACH_EMAIL:
						isValid = methods.validateSecondaryCoachEmail(val) as boolean;
						break;
					case TableColumnFields.MANAGER_NAME:
						isValid = methods.validateManagerName(val) as boolean;
						break;
					case TableColumnFields.MANAGER_EMAIL:
						isValid = methods.validateManagerEmail(val) as boolean;
						break;
				}

				if (!isValid) {
					hasWarning = true;
					// Don't break early, continue checking all fields
				}
			}

			return hasWarning ? BulkTableRowStatuses.WARNING : BulkTableRowStatuses.SUCCESS;
		},
		[methods, requiredFields]
	);

	// Optimize getValidTeams for better performance with large datasets
	const getValidTeams = useCallback(
		(tableData: TableTeamData[]) => {
			if (!tableData?.length) return [];

			const correctTeamRecords: CreateClubTeamModel[] = [];
			// Cache required field keys for better performance
			const requiredFieldKeys = Object.keys(requiredFields).filter(key => requiredFields[key as TableColumnFields]);

			// Use for loop instead of forEach for better performance and ability to break
			for (let i = 0; i < tableData.length; i++) {
				const row = tableData[i];
				// Skip completely empty rows
				if (!row || Object.keys(row).length === 0) continue;

				const hasValues = Object.values(row).some(x => x !== undefined);
				const hasRequiredFields = requiredFieldKeys.every(key => row?.[key] !== undefined);

				if (!hasValues || !hasRequiredFields) continue;

				const teamInfo: Omit<CreateClubTeamModel, "behavior" | "gender"> & {
					behavior: AgeGroupEnum | null;
					gender: GenderValEnum | null;
				} = {
					name: "",
					behavior: null,
					age: 0,
					gender: null,
					level: "",
					primaryCoachName: "",
					primaryCoachGender: null,
					primaryCoachEmail: "",
					secondaryCoachName: "",
					secondaryCoachGender: null,
					secondaryCoachEmail: "",
					managerName: "",
					managerEmail: ""
				};

				let canBeImported = true;

				// Use for-of loop on a filtered set of fields for better performance
				const relevantFields = Object.keys(TableColumnFields);

				for (const field of relevantFields) {
					const cellVal = row[field] || null;
					const isRequiredField = requiredFields[field as TableColumnFields] || false;

					if (isRequiredField && !cellVal) {
						canBeImported = false;
						break;
					}

					if (!cellVal && !isRequiredField) continue;

					switch (field) {
						case TableColumnFields.NAME:
							if (methods.validateName(cellVal)) {
								teamInfo.name = cellVal.trim();
							} else if (isRequiredField) {
								canBeImported = false;
							}
							break;

						case TableColumnFields.GENDER:
						case TableColumnFields.PRIMARY_COACH_GENDER:
						case TableColumnFields.SECONDARY_COACH_GENDER:
							// Fast lookup with Sets instead of includes
							if (MALE_GENDER_VALUES.has(cellVal?.toLowerCase())) {
								if (field === TableColumnFields.GENDER) {
									teamInfo.gender = GenderValEnum.MALE;
								}
								if (field === TableColumnFields.PRIMARY_COACH_GENDER) {
									teamInfo.primaryCoachGender = GenderValEnum.MALE;
								}
								if (field === TableColumnFields.SECONDARY_COACH_GENDER) {
									teamInfo.secondaryCoachGender = GenderValEnum.MALE;
								}
							} else if (FEMALE_GENDER_VALUES.has(cellVal?.toLowerCase())) {
								if (field === TableColumnFields.GENDER) {
									teamInfo.gender = GenderValEnum.FEMALE;
								}
								if (field === TableColumnFields.PRIMARY_COACH_GENDER) {
									teamInfo.primaryCoachGender = GenderValEnum.FEMALE;
								}
								if (field === TableColumnFields.SECONDARY_COACH_GENDER) {
									teamInfo.secondaryCoachGender = GenderValEnum.FEMALE;
								}
							} else if (isRequiredField) {
								canBeImported = false;
							}
							break;

						case TableColumnFields.AGE:
							if (methods.validateAge(cellVal)) {
								if (cellVal?.toLowerCase()?.startsWith("u")) {
									teamInfo.behavior = AgeGroupEnum.UNDER;
									teamInfo.age = Number(cellVal.replace(/\D/g, ""));
								}
								if (cellVal?.toLowerCase()?.startsWith("o")) {
									teamInfo.behavior = AgeGroupEnum.OVER;
									teamInfo.age = Number(cellVal.replace(/\D/g, ""));
								}
								if (cellVal.toLowerCase() === "open") {
									teamInfo.behavior = AgeGroupEnum.OPEN;
								}
							} else if (isRequiredField) {
								canBeImported = false;
							}
							break;

						case TableColumnFields.LEVEL:
							if (methods.validateLevel(cellVal)) {
								teamInfo.level = cellVal.trim();
							} else if (isRequiredField) {
								canBeImported = false;
							}
							break;

						case TableColumnFields.PRIMARY_COACH_NAME:
							if (methods.validatePrimaryCoachName(cellVal)) {
								teamInfo.primaryCoachName = cellVal.trim();
							} else if (isRequiredField) {
								canBeImported = false;
							}
							break;

						case TableColumnFields.PRIMARY_COACH_EMAIL:
							if (methods.validatePrimaryCoachEmail(cellVal)) {
								teamInfo.primaryCoachEmail = cellVal.trim();
							} else if (isRequiredField) {
								canBeImported = false;
							}
							break;

						case TableColumnFields.SECONDARY_COACH_NAME:
							if (methods.validateSecondaryCoachName(cellVal)) {
								teamInfo.secondaryCoachName = cellVal.trim();
							} else if (isRequiredField) {
								canBeImported = false;
							}
							break;

						case TableColumnFields.SECONDARY_COACH_EMAIL:
							if (methods.validateSecondaryCoachEmail(cellVal)) {
								teamInfo.secondaryCoachEmail = cellVal.trim();
							} else if (isRequiredField) {
								canBeImported = false;
							}
							break;

						case TableColumnFields.MANAGER_NAME:
							if (methods.validateManagerName(cellVal)) {
								teamInfo.managerName = cellVal.trim();
							} else if (isRequiredField) {
								canBeImported = false;
							}
							break;

						case TableColumnFields.MANAGER_EMAIL:
							if (methods.validateManagerEmail(cellVal)) {
								teamInfo.managerEmail = cellVal.trim();
							} else if (isRequiredField) {
								canBeImported = false;
							}
							break;
					}

					if (!canBeImported) break;
				}

				if (canBeImported) {
					correctTeamRecords.push(teamInfo as CreateClubTeamModel);
				}
			}

			return correctTeamRecords;
		},
		[methods, requiredFields]
	);

	const getData = useCallback(() => {
		return { requiredFields };
	}, [requiredFields]);

	return { ...methods, getData, checkRowStatus, getValidTeams };
};

export default useBulkTeamTableValidation;
